import React, { useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import {
  ACTION_TYPES,
  actionType,
  CARRIERS,
  carrierById,
  SERVICES,
  servicePickerLabel,
  PACKAGES,
  GROUPS,
  DEFAULT_COMPARABLE_SERVICES
} from '../config/rateShopConstants';
import ValuePicker from './ValuePicker';
import HelpTip from './HelpTip';
import useRowKeys from '../hooks/useRowKeys';
import BrandIcon from './BrandIcon';
import { carrierIconFor, serviceIconFor } from '../config/brandIcons';
const serviceOption = option => (
  <span className="d-inline-flex align-items-center gap-2">
    <BrandIcon src={serviceIconFor(option.value)} alt={option.label} />
    <span>
      {option.label}
      <small className="d-block text-muted">
        {option.code || option.value}
      </small>
    </span>
  </span>
);
const carrierOption = option => {
  const code = carrierById(option.value)?.carrierCode;
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <BrandIcon src={carrierIconFor(code)} alt={code || option.label} />
      <span>{option.label}</span>
    </span>
  );
};
const ActionChoices = ({ onSelect, disabled }) => (
  <Row className="g-2 mb-3">
    {ACTION_TYPES.map(type => (
      <Col md={6} key={type.id}>
        <Button
          type="button"
          variant="outline-primary"
          className="h-100 w-100 text-start p-3"
          disabled={disabled}
          onClick={() => onSelect(type.id)}
        >
          <strong className="d-block">{type.label}</strong>
          <span className="small d-block mt-1">{type.help}</span>
        </Button>
      </Col>
    ))}
  </Row>
);
const ActionParam = ({
  param,
  value,
  onChange,
  disabled,
  tagNames,
  tagsLoading,
  services
}) => {
  if (param.input === 'number' || param.input === 'text')
    return (
      <Form.Control
        aria-label={param.label}
        type={param.input}
        step="any"
        disabled={disabled}
        value={value ?? ''}
        onChange={e =>
          onChange(
            param.input === 'number' && e.target.value !== ''
              ? Number(e.target.value)
              : e.target.value
          )
        }
      />
    );
  const options = {
    service: services,
    services,
    carrier: CARRIERS.map(c => ({
      value: c.carrierId,
      label: `${c.name} · ${c.carrierCode}`
    })),
    package: PACKAGES.map(p => ({ value: p.code, label: p.name })),
    groups: GROUPS.map(g => ({ value: g.id, label: g.label })),
    tag: tagNames.map(t => ({ value: t, label: t }))
  };
  options.carriers = options.carrier;
  return (
    <ValuePicker
      label={param.label}
      disabled={disabled}
      value={value}
      onChange={onChange}
      multiple={['services', 'carriers', 'groups'].includes(param.input)}
      creatable={param.input === 'tag'}
      loading={param.input === 'tag' && tagsLoading}
      options={options[param.input]}
      formatOptionLabel={
        ['service', 'services'].includes(param.input)
          ? serviceOption
          : ['carrier', 'carriers'].includes(param.input)
          ? carrierOption
          : undefined
      }
    />
  );
};
const ActionCard = ({
  action,
  index,
  onChange,
  onRemove,
  disabled,
  tagNames,
  tagsLoading,
  settings
}) => {
  const [changing, setChanging] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const allowed = settings?.comparableServices || DEFAULT_COMPARABLE_SERVICES;
  const definition = actionType(action.type);
  const savedServices = Array.isArray(action.services)
    ? action.services
    : action.services
    ? [action.services]
    : [action.serviceCode];
  const options = SERVICES.filter(
    s => showAll || allowed.includes(s.code) || savedServices.includes(s.code)
  ).map(s => ({
    value: s.code,
    code: s.code,
    label: servicePickerLabel(s.code)
  }));
  const selected =
    action.type === 'only_services'
      ? Array.isArray(action.services)
        ? action.services
        : [action.services]
      : [action.serviceCode];
  const unavailable = [
    'force_service',
    'prefer_service',
    'only_services'
  ].includes(action.type)
    ? selected.filter(code => code && !allowed.includes(code))
    : [];
  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between gap-2">
        <h6>
          {definition?.label || action.type}
          <HelpTip label={definition?.label || 'Action'}>
            {definition?.help}
          </HelpTip>
        </h6>
        {!disabled && (
          <span>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setChanging(!changing)}
            >
              Change action
            </Button>
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={onRemove}
            >
              Remove
            </Button>
          </span>
        )}
      </div>
      <p className="small text-muted">{definition?.help}</p>
      {changing && (
        <ActionChoices
          disabled={disabled}
          onSelect={type => {
            onChange({ type });
            setChanging(false);
          }}
        />
      )}
      {definition?.params.some(p =>
        ['service', 'services'].includes(p.input)
      ) && (
        <Form.Check
          id={`rate-shop-all-services-${index}`}
          type="switch"
          className="small mb-3"
          disabled={disabled}
          checked={showAll}
          onChange={e => setShowAll(e.target.checked)}
          label={
            <span>
              Show all services{' '}
              <HelpTip label="Show all services">
                The picker normally shows only services allowed to win in
                Settings. Show all to see other services; selecting one does not
                automatically allow it to win.
              </HelpTip>
            </span>
          }
        />
      )}
      <Row className="g-3">
        {definition?.params.map(param => (
          <Col
            key={param.key}
            md={
              ['services', 'carriers', 'groups'].includes(param.input) ? 12 : 6
            }
          >
            <Form.Group>
              <Form.Label className="small">
                {param.label}
                {param.required && ' *'}
                <HelpTip label={param.label}>
                  {param.input === 'number'
                    ? 'Enter a price or percent limit. Either limit can make the preferred service win.'
                    : definition.help}
                </HelpTip>
              </Form.Label>
              <ActionParam
                param={param}
                value={action[param.key]}
                disabled={disabled}
                tagNames={tagNames}
                tagsLoading={tagsLoading}
                services={options}
                onChange={value => onChange({ ...action, [param.key]: value })}
              />
              {param.key === 'packageCode' && (
                <Form.Text>
                  This package choice is currently ignored by the backend; the
                  winning price determines the package.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        ))}
      </Row>
      {unavailable.length > 0 && (
        <Alert variant="warning" className="small mt-3 mb-0">
          {unavailable.map(servicePickerLabel).join(', ')}: not comparable — the
          backend will ignore/fall back. Allow this service to win in Settings
          if you want it chosen.
        </Alert>
      )}
    </div>
  );
};
const ActionBuilder = ({
  actions,
  onChange,
  disabled,
  tagNames,
  tagsLoading,
  settings
}) => {
  const rowKeys = useRowKeys();
  const [adding, setAdding] = useState(!actions.length);
  return (
    <div className="mb-4">
      <h5>Then do this</h5>
      <p className="small text-muted">
        Choose what should happen to orders that meet your conditions.
      </p>
      {actions.map((action, index) => (
        <ActionCard
          key={rowKeys.key(action)}
          action={action}
          index={index}
          disabled={disabled}
          tagNames={tagNames}
          tagsLoading={tagsLoading}
          settings={settings}
          onChange={next =>
            onChange(
              actions.map((a, i) =>
                i === index ? rowKeys.replace(a, next) : a
              )
            )
          }
          onRemove={() => onChange(actions.filter((_, i) => i !== index))}
        />
      ))}
      {!disabled &&
        (adding || !actions.length ? (
          <>
            <ActionChoices
              onSelect={type => {
                onChange([...actions, { type }]);
                setAdding(false);
              }}
            />
            {actions.length > 0 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setAdding(false)}
              >
                Cancel
              </Button>
            )}
          </>
        ) : (
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => setAdding(true)}
          >
            Add another action
          </Button>
        ))}
    </div>
  );
};
export default ActionBuilder;
